import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";
import {
  classifySquadActivityFailure,
  isOffKind,
  REQUEST_TIMEOUT_MS,
  throttlePauseMs,
  WINDOW_MS,
} from "./schedule";

/**
 * squadActivity - the squad's recent file and task activity (dashboard social,
 * Slice 2, D-1 to D-5).
 *
 * It backs `GET /me/squad-activity?challenge={id}[&before=]` and
 * `POST /me/squad-activity/seen` (backend PR #183). The server resolves the
 * caller's squad from current membership, applies the cohort allowlist and
 * the credential gate, and answers for the CALLER ONLY: which teammate events
 * are unseen for them, and a presentation token binding exactly the ids of
 * the page it served.
 *
 * SESSION ONLY. It is not in the persist whitelist and `LOGOUT` resets it: it
 * carries one squad's work titles and one learner's read state.
 *
 * THE SCOPE KEY IS (userId, challengeId, squadId). A new scope clears every
 * field at `pending`, the acknowledgement memory included, so one learner's
 * seen ids can never suppress a marker for the next.
 *
 * A RESPONSE FOR ANOTHER SQUAD IS DISCARDED. The server resolves the squad
 * itself, so after a squad move it can answer for a squad the dashboard does
 * not hold yet. That answer is never shown: status becomes `scope-wait`, and
 * nothing renders until the dashboard's own scope catches up (D-5).
 *
 * TIMESTAMPS ARE STORED AS SERVED AND NEVER READ (D-11, F-1): every event
 * time is written in Africa/Lagos wall time and served as UTC, so any age
 * computed from it is an hour wrong.
 */

/** The build flag, read at call time. Nothing is enabled by any `.env` file. */
export const squadActivityBuildEnabled = () =>
  process.env.REACT_APP_SQUAD_ACTIVITY === "true";

/**
 * ONE REQUEST LANE PER TAB (D-3). Every activity request runs inside this
 * promise chain, so one is in flight at a time and responses are applied in
 * send order: a passive read queued behind a seen call cannot land first and
 * restore the count the seen call just lowered.
 */
let lane = Promise.resolve();
export const runInSquadActivityLane = (fn) => {
  const run = lane.then(fn, fn);
  lane = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};

/**
 * THE SEND GATE (code review r1, G1). The owner checks whether a request may
 * go out when it DISPATCHES it, but the lane may hold it behind a slow request
 * for seconds, and the economy's pending poll can start in between. So the
 * owner also registers this gate, and the lane asks it again immediately
 * before each network send. A refused request never reaches axios: it settles
 * as `skipped`, which is neither a failure nor a request that was sent.
 */
let sendGate = null;
export const setSquadActivitySendGate = (gate) => {
  sendGate = gate;
  return () => {
    if (sendGate === gate) sendGate = null;
  };
};
const SKIPPED = "squad-activity-skipped";
const sendThroughLane = (send) =>
  runInSquadActivityLane(() => {
    if (sendGate && !sendGate()) {
      const skipped = new Error(SKIPPED);
      skipped.name = SKIPPED;
      throw skipped;
    }
    return send();
  });

/** Tests only: a fresh lane, no shared head read and no gate. */
export const __resetSquadActivityLane = () => {
  lane = Promise.resolve();
  sharedHead = null;
  sendGate = null;
};

/** The scope a held payload belongs to. Missing ids never produce a key. */
export const squadActivityScopeKey = ({ userId, challengeId, squadId } = {}) =>
  userId && challengeId && squadId
    ? `${userId}:${challengeId}:${squadId}`
    : null;

const isCount = (value) => Number.isInteger(value) && value >= 0;

/** Does a served payload answer for exactly the scope that was asked? */
const answersScope = (data, arg) =>
  !!data &&
  Number.isInteger(data.challenge_id) &&
  Number.isInteger(data.squad_id) &&
  data.challenge_id === Number(arg.challengeId) &&
  data.squad_id === Number(arg.squadId);

const failureOf = (err) => {
  if (err?.name === SKIPPED) {
    return { status: null, code: null, kind: "skipped", at: Date.now() };
  }
  const aborted = err?.name === "CanceledError" || err?.name === "AbortError";
  const status = err?.response?.status ?? null;
  const code = err?.response?.data?.data?.code ?? null;
  const timeout =
    err?.code === "ECONNABORTED" ||
    err?.code === "ETIMEDOUT" ||
    /timeout/i.test(String(err?.message ?? ""));
  const network = !aborted && status === null;
  return {
    status,
    code: typeof code === "string" ? code : null,
    kind: classifySquadActivityFailure({
      status,
      code,
      network,
      timeout: status === null && timeout,
      aborted,
    }),
    at: Date.now(),
  };
};

let squadActivityRequestSeq = 0;

const readThunk = createAsyncThunk(
  "squadActivity/fetch",
  async ({ challengeId, before } = {}, thunkAPI) => {
    try {
      const response = await sendThroughLane(() =>
        axiosInstance(
          "/me/squad-activity",
          "get",
          undefined,
          /* `params` and `timeout` are promoted to axios config by
             axiosInstance. `before` is standard base64 (`+`, `/`, `=`), so it
             travels ONLY through params, which encodes it. The allowlist is
             exactly `challenge` and an optional `before`: the server prohibits
             `user_id`, `squad_id`, `include`, `actor` and `read_cursor`. */
          {
            params:
              typeof before === "string" && before.length > 0
                ? { challenge: challengeId, before }
                : { challenge: challengeId },
            timeout: REQUEST_TIMEOUT_MS,
          },
        ),
      );
      return { data: response?.data?.data ?? null, receivedAt: Date.now() };
    } catch (err) {
      return thunkAPI.rejectWithValue(failureOf(err));
    }
  },
  {
    idGenerator: () => `sqa-${++squadActivityRequestSeq}`,
    getPendingMeta: () => ({ at: Date.now() }),
  },
);

/**
 * A HEAD READ ASKED FOR WHILE ANOTHER IS QUEUED OR IN FLIGHT FOR THE SAME
 * SCOPE RETURNS THE SAME PROMISE (D-3). Pages are never shared.
 */
let sharedHead = null;

/**
 * @param {object} arg
 * @param {number} arg.userId the client half of the scope key; never sent
 * @param {number} arg.challengeId sent as `challenge`
 * @param {number} arg.squadId the squad the dashboard holds; never sent, and
 *   the response must answer for it
 * @param {string} [arg.before] a page cursor (`next_cursor`)
 * @param {string} arg.reason why the read is made, for the hook's bookkeeping
 * @param {boolean} [arg.passive] counts against the passive budget
 */
export const fetchSquadActivity = (arg) => (dispatch) => {
  const isHead = !(typeof arg?.before === "string" && arg.before.length > 0);
  if (!isHead) return dispatch(readThunk(arg));
  const key = squadActivityScopeKey(arg);
  if (sharedHead && sharedHead.key === key) return sharedHead.promise;
  const promise = dispatch(readThunk(arg));
  const entry = { key, promise };
  sharedHead = entry;
  promise.then(() => {
    if (sharedHead === entry) sharedHead = null;
  });
  return promise;
};
fetchSquadActivity.pending = readThunk.pending;
fetchSquadActivity.fulfilled = readThunk.fulfilled;
fetchSquadActivity.rejected = readThunk.rejected;
fetchSquadActivity.typePrefix = readThunk.typePrefix;

/**
 * @param {object} arg
 * @param {string[]} arg.eventIds presented, new, not yet acknowledged ids
 * @param {string} arg.token that presentation's `presentation_token`
 */
export const markSquadActivitySeen = createAsyncThunk(
  "squadActivity/seen",
  async ({ challengeId, eventIds, token } = {}, thunkAPI) => {
    try {
      const response = await sendThroughLane(() =>
        axiosInstance(
          "/me/squad-activity/seen",
          "post",
          /* Exactly these three keys. `before` is prohibited on this route. */
          {
            challenge: challengeId,
            event_ids: eventIds,
            presentation_token: token,
          },
          { timeout: REQUEST_TIMEOUT_MS },
        ),
      );
      return { data: response?.data?.data ?? null, receivedAt: Date.now() };
    } catch (err) {
      return thunkAPI.rejectWithValue(failureOf(err));
    }
  },
  {
    idGenerator: () => `sqs-${++squadActivityRequestSeq}`,
    getPendingMeta: () => ({ at: Date.now() }),
  },
);

const ACK_CAP = 200;

const initialState = {
  scopeKey: null,
  /** 'idle' | 'loading' | 'ready' | 'scope-wait' | 'off' */
  status: "idle",
  offReason: null,
  offAt: null,
  /** The served head read's `data`, plus `receivedAt`. */
  head: null,
  unseenCount: null,
  lastHeadAt: null,
  /** The last failure, `{kind, status, code, at, count, throttles}`, or null. */
  failure: null,
  /**
   * Did the latest HEAD read fail? Only a head read can make the list on
   * screen out of date, so only it drives the stale line (G4); a failed seen
   * call or page never does.
   */
  headFailed: false,
  pausedUntil: null,
  /** Every request's start time in the last 60s, `{at, passive}`. */
  requestLog: [],
  /** Tokens and ids this tab has acknowledged in this scope. */
  acknowledged: { tokens: [], ids: [] },
  /**
   * Seen calls sent and not yet settled (G2). A panel closed and reopened
   * before the first one answers must not send the same ids again.
   */
  pendingAcks: { tokens: [], ids: [] },
  /** The latest head read; an older one never applies. */
  requestId: null,
};

const clearScope = (state, scopeKey) => {
  const log = state.requestLog;
  Object.assign(state, initialState, { scopeKey, requestLog: log });
  state.acknowledged = { tokens: [], ids: [] };
  state.pendingAcks = { tokens: [], ids: [] };
};

const logRequest = (state, at, passive, requestId) => {
  state.requestLog = state.requestLog
    .filter((entry) => at - entry.at < WINDOW_MS)
    .concat({ at, passive: passive === true, requestId });
};

/** A skipped request was never sent, so it does not count against the budget. */
const unlogRequest = (state, requestId) => {
  state.requestLog = state.requestLog.filter(
    (entry) => entry.requestId !== requestId,
  );
};

const dropPending = (state, arg) => {
  const sent = arg?.eventIds ?? [];
  state.pendingAcks.tokens = state.pendingAcks.tokens.filter(
    (token) => token !== arg?.token,
  );
  state.pendingAcks.ids = state.pendingAcks.ids.filter(
    (id) => !sent.includes(id),
  );
};

const recordFailure = (state, failure) => {
  const previous = state.failure;
  const throttled = failure.kind === "throttled";
  const throttles = throttled ? (previous?.throttles ?? 0) + 1 : 0;
  const count =
    failure.kind === "transient" || failure.kind === "contract"
      ? (previous?.count ?? 0) + 1
      : (previous?.count ?? 0);
  state.failure = {
    kind: failure.kind,
    status: failure.status,
    code: failure.code,
    at: failure.at,
    count,
    throttles,
  };
  if (throttled) state.pausedUntil = failure.at + throttlePauseMs(throttles);
  if (isOffKind(failure.kind)) {
    state.status = "off";
    state.offReason = failure.kind;
    state.offAt = failure.at;
    state.head = null;
    state.unseenCount = null;
  }
};

const inScope = (state, arg) =>
  squadActivityScopeKey(arg) !== null &&
  squadActivityScopeKey(arg) === state.scopeKey;

const squadActivitySlice = createSlice({
  name: "squadActivity",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(readThunk.pending, (state, action) => {
        const { arg } = action.meta;
        const key = squadActivityScopeKey(arg);
        if (key !== state.scopeKey) clearScope(state, key);
        logRequest(state, action.meta.at, arg?.passive, action.meta.requestId);
        const isHead = !(typeof arg?.before === "string" && arg.before);
        if (!isHead) return;
        state.requestId = action.meta.requestId;
        if (state.status === "idle" || state.status === "off") {
          state.status = "loading";
        }
      })
      .addCase(readThunk.fulfilled, (state, action) => {
        const { arg } = action.meta;
        if (!inScope(state, arg)) return;
        const isHead = !(typeof arg?.before === "string" && arg.before);
        if (isHead && action.meta.requestId !== state.requestId) return;
        const { data, receivedAt } = action.payload ?? {};
        if (!answersScope(data, arg)) {
          /* Another squad or challenge: discarded, and nothing renders. */
          state.status = "scope-wait";
          state.head = null;
          state.unseenCount = null;
          return;
        }
        if (data.read_state !== "known") {
          recordFailure(state, {
            kind: "contract",
            status: 200,
            code: null,
            at: receivedAt,
          });
          if (!state.head) state.status = "idle";
          return;
        }
        state.unseenCount = isCount(data.unseen_count)
          ? data.unseen_count
          : null;
        state.failure = null;
        state.pausedUntil = null;
        if (!isHead) return;
        state.headFailed = false;
        state.head = { ...data, receivedAt };
        state.lastHeadAt = receivedAt;
        state.status = "ready";
        state.offReason = null;
        state.offAt = null;
      })
      .addCase(readThunk.rejected, (state, action) => {
        const { arg } = action.meta;
        if (!inScope(state, arg)) return;
        const isHead = !(typeof arg?.before === "string" && arg.before);
        if (isHead && action.meta.requestId !== state.requestId) return;
        const failure = action.payload;
        if (failure?.kind === "skipped")
          unlogRequest(state, action.meta.requestId);
        if (
          !failure ||
          failure.kind === "aborted" ||
          failure.kind === "skipped"
        ) {
          if (isHead && state.status === "loading") {
            state.status = state.head ? "ready" : "idle";
          }
          return;
        }
        recordFailure(state, failure);
        if (isHead) state.headFailed = true;
        if (isHead && state.status === "loading") {
          state.status = state.head ? "ready" : "idle";
        }
      })
      .addCase(markSquadActivitySeen.pending, (state, action) => {
        const { arg } = action.meta;
        if (!inScope(state, arg)) return;
        logRequest(state, action.meta.at, false, action.meta.requestId);
        state.pendingAcks.tokens = state.pendingAcks.tokens
          .filter((token) => token !== arg.token)
          .concat(arg.token);
        state.pendingAcks.ids = state.pendingAcks.ids
          .filter((id) => !(arg.eventIds ?? []).includes(id))
          .concat(arg.eventIds ?? []);
      })
      .addCase(markSquadActivitySeen.fulfilled, (state, action) => {
        const { arg } = action.meta;
        if (!inScope(state, arg)) return;
        const data = action.payload?.data ?? {};
        dropPending(state, arg);
        /* Every SENT id is remembered, not only the accepted ones: an id the
           server dropped is one it will not mark, so sending it again is
           pointless (D-8 rule 2). */
        const ids = state.acknowledged.ids.filter(
          (id) => !(arg.eventIds ?? []).includes(id),
        );
        state.acknowledged.ids = ids.concat(arg.eventIds ?? []).slice(-ACK_CAP);
        state.acknowledged.tokens = state.acknowledged.tokens
          .filter((token) => token !== arg.token)
          .concat(arg.token)
          .slice(-ACK_CAP);
        if (isCount(data.unseen_count)) state.unseenCount = data.unseen_count;
        state.failure = null;
        state.pausedUntil = null;
      })
      .addCase(markSquadActivitySeen.rejected, (state, action) => {
        if (!inScope(state, action.meta.arg)) return;
        dropPending(state, action.meta.arg);
        const failure = action.payload;
        if (failure?.kind === "skipped")
          unlogRequest(state, action.meta.requestId);
        if (
          !failure ||
          failure.kind === "aborted" ||
          failure.kind === "skipped"
        )
          return;
        recordFailure(state, failure);
      });
  },
});

/** The one selector (D-2). */
export const selectSquadActivityView = (state) => state.squadActivity;

export default squadActivitySlice.reducer;
